// A full reload (updateData) fetches the tables one after the other, then
// replaces MemoryDB with them. The rows refreshData receives meanwhile (the
// response of our save, a colleague's update-data) are newer than a table
// fetched before them: putting the fetched table back would undo them (a
// stale row, and the next save based on a version the server no longer has).
// They are recorded while a reload runs and patched into each fetched table
// before it replaces MemoryDB. reloadItems does the same for its rows.

const versionOf = (row) => Number(row?.version ?? 0);

// requestData resolves an empty array when the request failed (or the server
// had no data): such a table must not replace the rows in MemoryDB.
export function failedFetch() {
  return Object.defineProperty([], "failed", { value: true });
}

export const isFailedFetch = (rows) => Boolean(rows?.failed);

// A failed fetch keeps the table loaded before, even an empty one (its rows
// may all be removed by drafts): only a table never loaded is replaced.
export const keepsLoadedTable = (failed, loaded) =>
  Boolean(failed) && loaded instanceof Map;

export function createReloadOverlay() {
  let running = 0;
  // A change of tenant or branch: the reloads still running belong to the
  // previous one, their end must not touch the new ones
  let generation = 0;
  // table -> Map(id -> { action, data }), the last one received
  let received = new Map();

  return {
    // Returns the token to give to end()
    begin() {
      running += 1;
      return generation;
    },

    end(token = generation) {
      if (token !== generation) return;
      running = Math.max(0, running - 1);
      if (running === 0) received = new Map();
    },

    // Another tenant or branch: nothing received before applies
    reset() {
      generation += 1;
      running = 0;
      received = new Map();
    },

    isRunning: () => running > 0,

    // A row refreshData received (applied or kept by a draft)
    record(table, action, data) {
      if (running === 0 || data?.id === undefined) return;
      if (!received.has(table)) received.set(table, new Map());
      received.get(table).set(data.id, { action, data });
    },

    // The fetched rows of the table with the rows received meanwhile that
    // are at least as new (a deleted row goes, unless fetched newer).
    patch(table, fetched) {
      const rows = received.get(table);
      if (!rows?.size) return fetched;

      const byId = new Map(fetched.map((row) => [row?.id, row]));
      for (const [id, { action, data }] of rows.entries()) {
        const row = byId.get(id);

        if (action === "delete") {
          if (row && !(versionOf(row) > versionOf(data))) byId.delete(id);
        } else if (!row || versionOf(data) >= versionOf(row)) {
          byId.set(id, data);
        }
      }

      return [...byId.values()];
    }
  };
}
